import { useEffect, useState } from "react";
import { Mail, Save, Send, Trash2, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import api from "../../lib/api";

interface MailConfigState {
  configured: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUser?: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string | null;
  lastTestedAt?: string | null;
  lastError?: string | null;
}

const SECURITY_OPTIONS = [
  { value: "ssl", label: "SSL/TLS (poort 465)" },
  { value: "starttls", label: "STARTTLS (poort 587)" },
  { value: "none", label: "Geen versleuteling (poort 25)" },
];

function securityFromConfig(secure?: boolean, port?: number): string {
  if (secure) return "ssl";
  if (port === 587) return "starttls";
  if (port === 25) return "none";
  return "starttls";
}

function portForSecurity(sec: string): number {
  if (sec === "ssl") return 465;
  if (sec === "none") return 25;
  return 587;
}

export function MailTab({ companyId }: { companyId?: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [config, setConfig] = useState<MailConfigState | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const [security, setSecurity] = useState("starttls");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [testTo, setTestTo] = useState("");

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 5000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/mail-config");
      setConfig(data);
      const sec = securityFromConfig(data.smtpSecure, data.smtpPort);
      setSecurity(sec);
      setSmtpHost(data.smtpHost || "");
      setSmtpPort(data.smtpPort ? String(data.smtpPort) : String(portForSecurity(sec)));
      setSmtpUser(data.smtpUser || "");
      setSmtpPassword("");
      setFromName(data.fromName || "");
      setFromEmail(data.fromEmail || "");
      setReplyTo(data.replyTo || "");
      setTestTo(data.fromEmail || "");
    } catch {
      setConfig({ configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSecurityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sec = e.target.value;
    setSecurity(sec);
    setSmtpPort(String(portForSecurity(sec)));
  };

  const handleSave = async () => {
    setSaving(true);
    setToast(null);
    try {
      const payload: any = {
        smtpHost,
        smtpPort: parseInt(smtpPort, 10) || 587,
        smtpSecure: security === "ssl",
        smtpUser,
        fromName,
        fromEmail,
        replyTo: replyTo || undefined,
      };
      if (smtpPassword) payload.password = smtpPassword;
      const { data } = await api.put("/api/mail-config", payload);
      setConfig(data);
      setSmtpPassword("");
      flash("ok", "Mail-configuratie opgeslagen (wachtwoord versleuteld).");
    } catch (err: any) {
      flash("err", err?.response?.data?.error || "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setToast(null);
    try {
      const { data } = await api.post("/api/mail-config/test", testTo ? { to: testTo } : {});
      if (data.ok) {
        flash("ok", `Testmail verzonden naar ${testTo || config?.fromEmail || "je eigen adres"}.`);
        load();
      } else {
        flash("err", `Test mislukt: ${data.error || "onbekende fout"}`);
        load();
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 428) {
        flash("err", "Mail is nog niet geconfigureerd. Sla eerst je instellingen op.");
      } else {
        flash("err", err?.response?.data?.error || "Test mislukt.");
      }
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Mail-configuratie verwijderen? Je kunt dan geen facturen/offertes meer per e-mail versturen vanuit AIBoek.")) return;
    setDeleting(true);
    try {
      await api.delete("/api/mail-config");
      flash("ok", "Mail-configuratie verwijderd.");
      load();
    } catch (err: any) {
      flash("err", err?.response?.data?.error || "Verwijderen mislukt.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <p className="text-sm text-slate-500">Mail-instellingen laden…</p>
      </Card>
    );
  }

  const configured = config?.configured;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
          <Mail size={30} />
        </div>
        <div>
          <h2 className="text-lg font-bold">Mail-configuratie (SMTP)</h2>
          <p className="text-sm text-slate-500">
            Stel je eigen mailserver in zodat je facturen en offertes veilig vanuit je eigen adres kunt versturen.
          </p>
        </div>
      </div>

      {/* Status + beveiligingsbanner */}
      <div className={`mt-4 mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
        configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-700"
      }`}>
        {configured ? <CheckCircle2 size={22} className="shrink-0 mt-0.5" /> : <AlertTriangle size={22} className="shrink-0 mt-0.5" />}
        <div className="space-y-1">
          <p className="font-semibold">
            {configured ? "Mail is geconfigureerd" : "Mail is nog niet geconfigureerd"}
          </p>
          {configured && config?.lastTestedAt && (
            <p className="text-xs">
              Laatste test: {new Date(config.lastTestedAt).toLocaleString("nl-NL")}
              {config.lastError ? ` — fout: ${config.lastError}` : " — succes"}
            </p>
          )}
          {configured && !config?.lastTestedAt && (
            <p className="text-xs">Nog niet getest. Druk op "Test verbinding" om te controleren.</p>
          )}
          {!configured && (
            <p className="text-xs">Vul de gegevens in en druk op Opslaan om e-mailversturen in te schakelen.</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 rounded-xl bg-brand-50/60 border border-brand-100 px-4 py-2.5 text-xs text-brand-800">
        <ShieldCheck size={20} className="shrink-0" />
        <span>Het SMTP-wachtwoord wordt <strong>AES-256-GCM versleuteld</strong> opgeslagen en is alleen in-memory beschikbaar tijdens het versturen.</span>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Input label="SMTP-server" placeholder="bijv. smtp.jouwdomein.nl" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
        <Select label="Beveiliging" options={SECURITY_OPTIONS} value={security} onChange={handleSecurityChange} />
        <Input label="Poort" type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} />
        <Input label="Gebruikersnaam (e-mail)" type="email" placeholder="bijv. info@jouwdomein.nl" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
        <Input
          label="Wachtwoord"
          type="password"
          placeholder={configured ? "•••• alleen invullen om te wijzigen" : "SMTP-wachtwoord"}
          value={smtpPassword}
          onChange={(e) => setSmtpPassword(e.target.value)}
        />
        <Input label="From-naam" placeholder="bijv. Jouw Bedrijf BV" value={fromName} onChange={(e) => setFromName(e.target.value)} />
        <Input label="From-e-mail" type="email" placeholder="bijv. info@jouwdomein.nl" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
        <Input label="Reply-to (optioneel)" type="email" placeholder="bijv. admin@jouwdomein.nl" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
      </div>

      {toast && (
        <div className={`mt-5 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium ${
          toast.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-rose-50 border-rose-200 text-rose-700"
        }`}>
          {toast.kind === "ok" ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
          {toast.msg}
        </div>
      )}

      {/* Test-sectie */}
      <div className="mt-6 pt-5 border-t border-slate-100">
        <p className="text-sm font-bold text-slate-700 mb-2">Test verbinding</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            className="sm:max-w-xs"
            label="Testmail naar"
            type="email"
            placeholder={fromEmail || "eigen adres"}
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </div>
      </div>

      {/* Acties */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button onClick={handleSave} loading={saving}>
          <Save size={20} /> Opslaan
        </Button>
        <Button variant="outline" onClick={handleTest} loading={testing} disabled={!configured && !smtpHost}>
          <Send size={20} /> Test verbinding
        </Button>
        {configured && (
          <Button variant="ghost" onClick={handleDelete} loading={deleting} className="text-rose-600 hover:bg-rose-50">
            <Trash2 size={20} /> Verwijderen
          </Button>
        )}
      </div>
    </Card>
  );
}